import {
  init,
  captureException,
  captureMessage,
  browserTracingIntegration,
} from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import type { ScopeContext } from '@sentry/types';

import { env } from '@shared/config';
import { sentryLib } from './lib';

type InitSentryParams = {
  app: App;
  initialScope?: Partial<ScopeContext>;
  router?: Router;
};

const createSentryAPI = () => ({
  init: ({ app, initialScope, router }: InitSentryParams) => {
    /**
     * TODO: add Replay integration
     *
     * @see https://tracker.yandex.ru/TOURTEAM-264
     */
    init({
      app,
      debug: env.MODE !== 'production',
      dsn: env.SENTRY_DSN,
      integrations: [
        browserTracingIntegration({
          router,
          beforeStartSpan: options => {
            /** @note Since we pass `router` options to an integration:
             *  - SPA (CSR) apps' transaction names will be equal to route names
             *  - SSR apps' transaction names will be equal to current `location.path`
             *
             *  To differentiate between multiple apps, either SPA or SSR, we pass an `app` attribute
             *  that will be displayed in the Performance UI
             *
             *  TODO: write SSR location parser to match SPA names, so transactions will be properly grouped in the UI
             */
            return {
              ...options,
              attributes: {
                ...options.attributes,
                app: initialScope?.tags?.app as string,
              },
            };
          },
        }),
      ],
      tracesSampleRate: env.MODE !== 'production' ? 0.6 : 1.0,

      environment: env.MODE,
      sampleRate: env.MODE !== 'production' ? 0.6 : 1.0,
      initialScope,
      tracePropagationTargets: [
        env.BASE_URL,
        env.PFM_API_URL,
        env.STATIC_BASE_URL,
      ],
    });
  },
  captureException,
  captureMessage,
  captureExWithCause: sentryLib.captureExWithCause,
});

type SentryAPI = ReturnType<typeof createSentryAPI>;

function getDevProxy(target: SentryAPI) {
  function capture(originalMethod: { name: unknown }) {
    return (data: unknown, captureContext: unknown) => {
      globalThis.console.group(`DEV ${originalMethod.name}`);
      globalThis.console.info(data);
      globalThis.console.info('Context', captureContext);
      globalThis.console.groupEnd();
    };
  }

  const stub = {
    init() {
      globalThis.console.debug(
        '%c[SENTRY] Mocking enabled',
        'font-weight: bold',
      );
    },
    captureMessage: capture(captureMessage),
    captureException: capture(captureException),
    captureExWithCause: capture(sentryLib.captureExWithCause),
  };

  return new Proxy(target, {
    get(target, property) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return property in stub ? stub[property] : target[property];
    },
  });
}

function create() {
  const sentryAPI = createSentryAPI();
  return env.MODE === 'development' ? getDevProxy(sentryAPI) : sentryAPI;
}

export const Sentry = create();
